import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, AlertTriangle, Trash2, Edit, X, Info, Link as LinkIcon, ShieldCheck, Upload, Download, Eye, Calendar, Filter, FileText, ChevronUp, ChevronDown, Bug, Briefcase } from 'lucide-react'
import Papa from 'papaparse'
import { ConfirmModal } from '../components/ConfirmModal'
import { format, addDays, differenceInDays, parseISO } from 'date-fns'
import MultiUserSelect from '../components/MultiUserSelect'
import { useAuth } from '../context/AuthContext'
import { VulnerabilityModal } from './Vulnerabilities.jsx'
import { ANNEX_A } from './Controls.jsx'

const THREATS = ['Unauthorized Access', 'Data Breach', 'Ransomware', 'Phishing', 'Insider Threat', 'System Failure', 'Natural Disaster', 'Supply Chain Attack', 'DDoS', 'Social Engineering', 'Other']
const TREATMENT_TYPES = ['Mitigate', 'Transfer', 'Accept', 'Avoid']
const STATUSES = ['Open', 'In Progress', 'Mitigated', 'Accepted', 'Closed']

export function getRiskLabel(score) {
  if (score >= 20) return 'Critical'
  if (score >= 12) return 'High'
  if (score >= 6) return 'Medium'
  return 'Low'
}

export function getRiskColor(score) {
  if (score >= 20) return 'var(--risk-critical)'
  if (score >= 12) return 'var(--risk-high)'
  if (score >= 6) return 'var(--risk-medium)'
  return 'var(--risk-low)'
}

function RiskScoreBadge({ score }) {
  const color = getRiskColor(score)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: color + '22', color, border: `1px solid ${color}44`,
      padding: '3px 9px', borderRadius: 100, fontSize: '0.8125rem', fontWeight: 700,
      fontFamily: 'var(--font-mono)'
    }}>
      {typeof score === 'number' ? score.toFixed(1) : '—'}
    </span>
  )
}

export function RiskModal({ riskId, onClose, onSave, isSaving, assets, securityControls, processes = [], annexControls = [], user }) {
  const [form, setForm] = useState({
    custom_id: '',
    description: '', asset_id: '', asset_ids: [], process_ids: [], annex_control_ids: [], threat: 'Unauthorized Access', vulnerability: '',
    likelihood: 3, impact: 3, controls: [],
    treatment_type: 'Mitigate', treatment_plan: '', owner_ids: [], status: 'Open',
    timeline: '', comments: '', due_date: ''
  })

  // Sync timeline and due_date
  const handleTimelineChange = (val) => {
    setForm(prev => {
      const next = { ...prev, timeline: val }
      const days = parseInt(val)
      if (!isNaN(days)) {
        const base = prev.created_at ? parseISO(prev.created_at) : new Date()
        next.due_date = format(addDays(base, days), 'yyyy-MM-dd')
      }
      return next
    })
  }

  const handleDueDateChange = (val) => {
    setForm(prev => {
      const next = { ...prev, due_date: val }
      if (val) {
        const base = prev.created_at ? parseISO(prev.created_at) : new Date()
        const diff = differenceInDays(parseISO(val), base)
        next.timeline = diff > 0 ? `${diff} Days` : '0 Days'
      }
      return next
    })
  }
  const [isFetching, setIsFetching] = useState(!!riskId)

  useEffect(() => {
    if (riskId) {
      api.getRisk(riskId).then(data => {
        let asset_ids = []
        try {
          asset_ids = typeof data.asset_ids === 'string' ? JSON.parse(data.asset_ids) : (data.asset_ids || [])
        } catch (e) { 
          // Fallback to single asset_id if JSON fails
          if (data.asset_id) asset_ids = [data.asset_id]
        }
        let process_ids = []
        try {
          process_ids = typeof data.process_ids === 'string' ? JSON.parse(data.process_ids) : (data.process_ids || [])
        } catch (e) { process_ids = [] }
        let annex_control_ids = []
        try {
          annex_control_ids = typeof data.annex_control_ids === 'string' ? JSON.parse(data.annex_control_ids) : (data.annex_control_ids || [])
        } catch (e) { annex_control_ids = [] }
        
        let existing_control_ids = []
        try {
          existing_control_ids = typeof data.existing_control_ids === 'string' ? JSON.parse(data.existing_control_ids) : (data.existing_control_ids || [])
        } catch (e) { existing_control_ids = [] }

        const mappedControls = existing_control_ids.map(item => {
          const isObj = typeof item === 'object' && item !== null;
          const ctrlId = isObj ? Number(item.id) : Number(item);
          const sc = securityControls.find(s => Number(s.id) === ctrlId);
          if (!sc) return null;
          return {
             id: sc.id,
             control_id: sc.control_id,
             name: sc.name,
             effectiveness: isObj && item.effectiveness != null ? item.effectiveness : (sc.effectiveness || 3),
             implementation_status: sc.implementation_status || 'Implemented'
          }
        }).filter(Boolean)

        setForm({ ...data, asset_ids, process_ids, annex_control_ids, existing_control_ids, controls: mappedControls })
        setIsFetching(false)
      })
    }
  }, [riskId])

  const qc = useQueryClient()
  const { data: vulnerabilities = [] } = useQuery({ queryKey: ['vulnerabilities'], queryFn: api.getVulnerabilities })
  const { data: allRisks = [] } = useQuery({ queryKey: ['risks'], queryFn: api.getRisks })
  
  const [showVulnModal, setShowVulnModal] = useState(false)
  const [editingVulnId, setEditingVulnId] = useState(null)

  const createVulnMut = useMutation({
    mutationFn: api.createVulnerability,
    onSuccess: () => { qc.invalidateQueries(['vulnerabilities']); setShowVulnModal(false) }
  })

  const updateVulnMut = useMutation({
    mutationFn: ({ id, data }) => api.updateVulnerability(id, data),
    onSuccess: () => { qc.invalidateQueries(['vulnerabilities']); setEditingVulnId(null) }
  })

  const mappedVulns = vulnerabilities.filter(v => {
    try {
      const rIds = typeof v.risk_ids === 'string' ? JSON.parse(v.risk_ids) : (v.risk_ids || [])
      return riskId && rIds.includes(riskId)
    } catch { return false }
  })

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const inherent = form.likelihood * form.impact
  const avgEffectiveness = form.controls?.length > 0 
    ? form.controls.reduce((s, c) => s + (c.effectiveness || 0), 0) / form.controls.length 
    : 0
  const residual = inherent * (1 - avgEffectiveness / 5)

  const toggleControl = (sc) => {
    const exists = form.controls.find(c => c.id === sc.id)
    if (exists) {
      setForm(f => ({
        ...f,
        controls: f.controls.filter(c => c.id !== sc.id),
        existing_control_ids: (f.existing_control_ids || []).filter(item => {
          const ctrlId = typeof item === 'object' && item !== null ? Number(item.id) : Number(item);
          return ctrlId !== sc.id;
        })
      }))
    } else {
      setForm(f => ({
        ...f,
        controls: [...f.controls, { 
          id: sc.id,
          control_id: sc.control_id, 
          name: sc.name, 
          effectiveness: sc.effectiveness || 3,
          implementation_status: sc.implementation_status || 'Implemented'
        }],
        existing_control_ids: [...(f.existing_control_ids || []), { id: sc.id, effectiveness: sc.effectiveness || 3 }]
      }))
    }
  }

  const updateEffectiveness = (id, val) => {
    setForm(f => ({
      ...f,
      controls: f.controls.map(c => c.id === id ? { ...c, effectiveness: val } : c),
      existing_control_ids: (f.existing_control_ids || []).map(item => {
         const isObj = typeof item === 'object' && item !== null;
         const ctrlId = isObj ? Number(item.id) : Number(item);
         if (ctrlId === id) return { id, effectiveness: val };
         return item;
      })
    }))
  }

  const toggleAsset = (id) => {
    const aid = Number(id)
    setForm(prev => {
      const exists = prev.asset_ids.includes(aid)
      const next = exists ? prev.asset_ids.filter(x => x !== aid) : [...prev.asset_ids, aid]
      return { ...prev, asset_ids: next }
    })
  }

  const [search, setSearch] = useState('')
  const [assetSearch, setAssetSearch] = useState('')
  const [processSearch, setProcessSearch] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [showAssetResults, setShowAssetResults] = useState(false)
  const [showProcessResults, setShowProcessResults] = useState(false)
  const [annexControlSearch, setAnnexControlSearch] = useState('')
  const [showAnnexControlResults, setShowAnnexControlResults] = useState(false)

  const toggleProcess = (id) => {
    const pid = Number(id)
    setForm(prev => {
      const exists = prev.process_ids.includes(pid)
      const next = exists ? prev.process_ids.filter(x => x !== pid) : [...prev.process_ids, pid]
      return { ...prev, process_ids: next }
    })
  }

  const toggleAnnexControl = (id) => {
    setForm(prev => {
      const exists = prev.annex_control_ids.includes(id)
      const next = exists ? prev.annex_control_ids.filter(x => x !== id) : [...prev.annex_control_ids, id]
      return { ...prev, annex_control_ids: next }
    })
  }

  const filteredAssets = assets.filter(a => 
    !form.asset_ids.includes(a.id) &&
    a.asset_name.toLowerCase().includes(assetSearch.toLowerCase())
  ).slice(0, 10)

  const filteredProcesses = processes.filter(p =>
    !form.process_ids.includes(p.id) &&
    p.process_name.toLowerCase().includes(processSearch.toLowerCase())
  ).slice(0, 10)

  const ALL_ANNEX_CONTROLS = ANNEX_A.flatMap(domain => domain.controls)

  const filteredAnnexControls = ALL_ANNEX_CONTROLS.filter(a =>
    !form.annex_control_ids.includes(a.id) &&
    (a.name.toLowerCase().includes(annexControlSearch.toLowerCase()) || a.id.toLowerCase().includes(annexControlSearch.toLowerCase()))
  ).slice(0, 10)

  const filteredResults = securityControls.filter(c => 
    !form.controls.find(attached => attached.id === c.id) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) || 
     (c.control_id && c.control_id.toLowerCase().includes(search.toLowerCase())))
  ).slice(0, 20)

  if (isFetching) return <div className="modal-overlay"><div className="modal"><div className="empty-state">Loading Risk Data…</div></div></div>

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <h2>{riskId ? 'Edit Risk Assessment' : 'New Risk Assessment'}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Risk ID (Auto)</label>
              <input className="form-input font-mono" value={form.custom_id || ''} disabled placeholder="RSK-XXXX" />
            </div>
            <div className="form-group">
              <label className="form-label">Risk Owner</label>
              <MultiUserSelect values={form.owner_ids || []} onChange={val => set('owner_ids', val)} disabled={user?.role === 'Contributor'} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Risk Description *</label>
            <input className="form-input" value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="e.g. Unauthorized access to..." />
          </div>
          
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Linked Assets</label>
            <div className="ctrl-search-container" style={{ position: 'relative' }}>
              <div 
                className="form-input flex items-center justify-between cursor-pointer"
                style={{ background: 'var(--bg-elevated)', minHeight: 42, flexWrap: 'wrap', gap: 6, padding: '6px 12px' }}
                onClick={() => setShowAssetResults(!showAssetResults)}
              >
                {form.asset_ids.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {form.asset_ids.map(aid => {
                      const a = assets.find(x => x.id === aid)
                      return (
                        <span key={aid} className="badge badge-accent" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {a?.asset_name || `ID-${aid}`}
                          <X size={10} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleAsset(aid) }} />
                        </span>
                      )
                    })}
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Link assets to this risk...</span>
                )}
                <ChevronDown size={16} style={{ opacity: 0.5, marginLeft: 'auto' }} />
              </div>

              {showAssetResults && (
                <div className="ctrl-search-results shadow-xl" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101, background: 'var(--bg-modal)', border: '1px solid var(--border)', borderRadius: 12, marginTop: 8, maxHeight: 250, overflowY: 'auto' }}>
                  <div style={{ padding: 10, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)' }}>
                    <input 
                      autoFocus
                      className="form-input" 
                      placeholder="Search assets..." 
                      value={assetSearch} 
                      onChange={e => setAssetSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 32, fontSize: '0.8125rem' }}
                    />
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    {filteredAssets.length > 0 ? filteredAssets.map(a => (
                      <div 
                        key={a.id} 
                        className="ctrl-search-item" 
                        style={{ padding: '8px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid var(--border-light)' }}
                        onClick={(e) => { e.stopPropagation(); toggleAsset(a.id); setAssetSearch(''); setShowAssetResults(false) }}
                      >
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{a.asset_name}</div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{a.category || a.asset_type}</div>
                      </div>
                    )) : (
                      <div style={{ padding: 15, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No assets found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LINKED PROCESSES */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={13} /> Linked Processes
            </label>
            <div className="ctrl-search-container" style={{ position: 'relative' }}>
              <div 
                className="form-input flex items-center justify-between cursor-pointer"
                style={{ background: 'var(--bg-elevated)', minHeight: 42, flexWrap: 'wrap', gap: 6, padding: '6px 12px' }}
                onClick={() => setShowProcessResults(!showProcessResults)}
              >
                {form.process_ids.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {form.process_ids.map(pid => {
                      const p = processes.find(x => x.id === pid)
                      return (
                        <span key={pid} className="badge badge-neutral" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                          <Briefcase size={10} />
                          {p?.process_name || `ID-${pid}`}
                          <X size={10} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleProcess(pid) }} />
                        </span>
                      )
                    })}
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Link processes to this risk...</span>
                )}
                <ChevronDown size={16} style={{ opacity: 0.5, marginLeft: 'auto' }} />
              </div>

              {showProcessResults && (
                <div className="ctrl-search-results shadow-xl" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101, background: 'var(--bg-modal)', border: '1px solid var(--border)', borderRadius: 12, marginTop: 8, maxHeight: 250, overflowY: 'auto' }}>
                  <div style={{ padding: 10, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)' }}>
                    <input 
                      autoFocus
                      className="form-input" 
                      placeholder="Search processes..." 
                      value={processSearch} 
                      onChange={e => setProcessSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 32, fontSize: '0.8125rem' }}
                    />
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    {filteredProcesses.length > 0 ? filteredProcesses.map(p => (
                      <div 
                        key={p.id} 
                        className="ctrl-search-item" 
                        style={{ padding: '8px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid var(--border-light)' }}
                        onClick={(e) => { e.stopPropagation(); toggleProcess(p.id); setProcessSearch(''); setShowProcessResults(false) }}
                      >
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{p.process_name}</div>
                        {p.description && <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{p.description.slice(0, 60)}</div>}
                      </div>
                    )) : (
                      <div style={{ padding: 15, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No processes found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MAPPED ANNEX CONTROLS */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ShieldCheck size={13} /> Mapped Annex Controls
            </label>
            <div className="ctrl-search-container" style={{ position: 'relative' }}>
              <div 
                className="form-input flex items-center justify-between cursor-pointer"
                style={{ background: 'var(--bg-elevated)', minHeight: 42, flexWrap: 'wrap', gap: 6, padding: '6px 12px' }}
                onClick={() => setShowAnnexControlResults(!showAnnexControlResults)}
              >
                {form.annex_control_ids.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {form.annex_control_ids.map(id => {
                      const a = ALL_ANNEX_CONTROLS.find(x => x.id === id)
                      return (
                        <span key={id} className="badge badge-neutral" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                          <ShieldCheck size={10} color="var(--accent)" />
                          {a ? `${a.id} - ${a.name}` : `ID-${id}`}
                          <X size={10} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleAnnexControl(id) }} />
                        </span>
                      )
                    })}
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Link Annex A controls to this risk...</span>
                )}
                <ChevronDown size={16} style={{ opacity: 0.5, marginLeft: 'auto' }} />
              </div>

              {showAnnexControlResults && (
                <div className="ctrl-search-results shadow-xl" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101, background: 'var(--bg-modal)', border: '1px solid var(--border)', borderRadius: 12, marginTop: 8, maxHeight: 250, overflowY: 'auto' }}>
                  <div style={{ padding: 10, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)' }}>
                    <input 
                      autoFocus
                      className="form-input" 
                      placeholder="Search Annex A controls..." 
                      value={annexControlSearch} 
                      onChange={e => setAnnexControlSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 32, fontSize: '0.8125rem' }}
                    />
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    {filteredAnnexControls.length > 0 ? filteredAnnexControls.map(a => (
                      <div 
                        key={a.id} 
                        className="ctrl-search-item" 
                        style={{ padding: '8px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid var(--border-light)' }}
                        onClick={(e) => { e.stopPropagation(); toggleAnnexControl(a.id); setAnnexControlSearch(''); setShowAnnexControlResults(false) }}
                      >
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{a.id} - {a.name}</div>
                        {a.description && <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{a.description.slice(0, 60)}</div>}
                      </div>
                    )) : (
                      <div style={{ padding: 15, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No controls found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ background: 'var(--bg-base)', borderRadius: '12px', padding: 20, border: '1px solid var(--border)', marginBottom: 20 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 16 }}>1. INHERENT RISK ASSESSMENT</div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Likelihood (1–5)</label>
                <select className="form-select" value={form.likelihood} onChange={e => set('likelihood', Number(e.target.value))}>
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Impact (1–5)</label>
                <select className="form-select" value={form.impact} onChange={e => set('impact', Number(e.target.value))}>
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Inherent Risk Score:</span>
              <RiskScoreBadge score={inherent} />
              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: getRiskColor(inherent) }}>{getRiskLabel(inherent)}</span>
            </div>
          </div>

          <div style={{ background: 'var(--bg-base)', borderRadius: '12px', padding: 20, border: '1px solid var(--border)', marginBottom: 20 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <span>2. EXISTING CONTROLS & EFFECTIVENESS</span>
              <ShieldCheck size={14} />
            </div>
            
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Attach Security Controls</label>
              <div className="ctrl-search-container" style={{ position: 'relative' }}>
                <div 
                  className="form-input flex items-center justify-between cursor-pointer"
                  style={{ background: 'var(--bg-elevated)', minHeight: 42 }}
                  onClick={() => setShowResults(!showResults)}
                >
                  <span style={{ color: search ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {search ? `Searching: ${search}` : 'Select a control from library...'}
                  </span>
                  <ChevronDown size={16} style={{ opacity: 0.5, transform: showResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </div>

                {showResults && (
                  <div className="ctrl-search-results shadow-xl" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-modal)', border: '1px solid var(--border)', borderRadius: 12, marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
                    <div style={{ padding: 12, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)' }}>
                      <input 
                        autoFocus
                        className="form-input" 
                        placeholder="Search by name or ID..." 
                        value={search} 
                        onChange={e => setSearch(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{ height: 36, fontSize: '0.8125rem' }}
                      />
                    </div>
                    <div style={{ padding: '4px 0' }}>
                      {filteredResults.length > 0 ? filteredResults.map(sc => (
                        <div 
                          key={sc.id} 
                          className="ctrl-search-item" 
                          style={{ padding: '10px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid var(--border-light)' }}
                          onClick={(e) => { e.stopPropagation(); toggleControl(sc); setSearch(''); setShowResults(false) }}
                        >
                          <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 2 }}>{sc.control_id || `ID-${sc.id}`}</div>
                          <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{sc.name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{sc.control_type} • {sc.owner || 'No owner'}</div>
                        </div>
                      )) : (
                        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                          No matching security controls found.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {form.controls.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {form.controls.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-elevated)', borderRadius: 10, border: '1px solid var(--border-light)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--accent)' }}>
                        {c.control_id || `ID-${c.id}`}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-primary)', fontWeight: 500 }}>{c.name}</div>
                    </div>
                    <div style={{ width: 180 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Effectiveness</label>
                        <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--accent)' }}>{c.effectiveness}/5</span>
                      </div>
                      <input type="range" min="1" max="5" value={c.effectiveness} onChange={e => updateEffectiveness(c.id, parseInt(e.target.value))} style={{ width: '100%', accentColor: 'var(--accent)' }} />
                    </div>
                    <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--text-muted)' }} onClick={() => toggleControl(c)}><X size={16} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 0', border: '2px dashed var(--border)', borderRadius: 12, color: 'var(--text-muted)' }}>
                <div style={{ marginBottom: 8 }}><ShieldCheck size={24} style={{ opacity: 0.3 }} /></div>
                <p style={{ fontSize: '0.8125rem' }}>No controls attached yet. Use the search box above to find and link protection measures.</p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 16, padding: '16px 20px', background: 'var(--bg-elevated)', borderRadius: '12px', alignItems: 'center', border: '2px solid var(--accent)', marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>RESIDUAL RISK SCORE</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RiskScoreBadge score={residual} />
                <span style={{ fontSize: '0.875rem', fontWeight: 700 }}>{getRiskLabel(residual)}</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Control Reduction</div>
              <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--success)' }}>
                {Math.round((avgEffectiveness / 5) * 100)}%
              </span>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bug size={16} color="var(--accent)" />
                Mapped Vulnerabilities
              </h3>
              {riskId && (
                <button className="btn btn-outline btn-sm" onClick={() => setShowVulnModal(true)}>
                  <Plus size={14} /> Add Vulnerability
                </button>
              )}
            </div>
            {!riskId ? (
              <div style={{ textAlign: 'center', padding: '16px', background: 'var(--bg-elevated)', borderRadius: 8, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Please save this risk first to add vulnerabilities.
              </div>
            ) : mappedVulns.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {mappedVulns.map(v => (
                  <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid var(--border-light)' }}>
                    <div>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{v.custom_id} - {v.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        Severity: <span style={{ color: v.severity === 'Critical' ? 'var(--danger)' : 'inherit' }}>{v.severity}</span> • Status: {v.status}
                      </div>
                    </div>
                    <button className="btn-icon" onClick={() => setEditingVulnId(v.id)} title="Edit Vulnerability">
                      <Edit size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '16px', border: '1px dashed var(--border)', borderRadius: 8, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                No vulnerabilities mapped to this risk.
              </div>
            )}
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Treatment Strategy</label>
              <select className="form-select" value={form.treatment_type} onChange={e => set('treatment_type', e.target.value)}>
                {TREATMENT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Action Plan</label>
            <textarea className="form-textarea" value={form.treatment_plan || ''} onChange={e => set('treatment_plan', e.target.value)} placeholder="Steps to treat this risk..." style={{ minHeight: 60 }} />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Timeline (Days)</label>
              <input className="form-input" value={form.timeline || ''} onChange={e => handleTimelineChange(e.target.value)} placeholder="e.g. 30" />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input type="date" className="form-input" value={form.due_date || ''} onChange={e => handleDueDateChange(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Additional Comments</label>
            <input className="form-input" value={form.comments || ''} onChange={e => set('comments', e.target.value)} placeholder="Any extra notes or context..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.description || isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Saving…' : riskId ? 'Update Assessment' : 'Register Risk'}
          </button>
        </div>
      </div>

      {showVulnModal && (
        <VulnerabilityModal 
          onClose={() => setShowVulnModal(false)}
          onSave={(data) => createVulnMut.mutate(data)}
          isSaving={createVulnMut.isPending}
          risks={allRisks}
          initialData={{ risk_ids: [riskId] }}
        />
      )}

      {editingVulnId && (
        <VulnerabilityModal 
          vulnId={editingVulnId}
          onClose={() => setEditingVulnId(null)}
          onSave={(data) => updateVulnMut.mutate({ id: editingVulnId, data })}
          isSaving={updateVulnMut.isPending}
          risks={allRisks}
        />
      )}
    </div>
  )
}

function RiskLifecycleModal({ riskId, onClose }) {
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['risk-history', riskId],
    queryFn: () => api.getRiskHistory(riskId)
  })

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 8, borderRadius: 8 }}>
              <Eye size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Risk Lifecycle</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Audit history and change tracking</p>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {isLoading ? (
            <div className="empty-state">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="empty-state">No history recorded yet.</div>
          ) : (
            <div className="timeline" style={{ padding: '10px 0' }}>
              {history.map((log, idx) => {
                let details = null
                try {
                  details = log.details ? (typeof log.details === 'string' ? JSON.parse(log.details) : log.details) : null
                } catch (e) {
                  console.error('Failed to parse audit details', e)
                }
                return (
                  <div key={log.id} style={{ display: 'flex', gap: 16, marginBottom: 24, position: 'relative' }}>
                    {idx !== history.length - 1 && (
                      <div style={{ position: 'absolute', left: 7, top: 20, bottom: -20, width: 2, background: 'var(--border-light)' }} />
                    )}
                    <div style={{ 
                      width: 16, height: 16, borderRadius: '50%', 
                      background: log.action === 'create' ? 'var(--success)' : log.action === 'delete' ? 'var(--danger)' : 'var(--accent)', 
                      marginTop: 4, zIndex: 1, border: '3px solid var(--bg-modal)'
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: '0.875rem', textTransform: 'capitalize' }}>
                          {log.action === 'create' ? 'Risk Identified' : log.action === 'update' ? 'Assessment Updated' : log.action}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', padding: '8px 12px', borderRadius: 8 }}>
                        {details?.changes ? (
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {details.changes.map((c, i) => <li key={i}>{c}</li>)}
                          </ul>
                        ) : details?.description ? (
                          <span>Initial description: {details.description}</span>
                        ) : (
                          <span>Record {log.action}d</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Close History</button>
        </div>
      </div>
    </div>
  )
}

export function Risks({ toast }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [viewHistoryId, setViewHistoryId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  
  // Filters state
  const [filters, setFilters] = useState({
    status: [], // Default to showing all
    from: '',
    to: ''
  })
  
  const { data: risks = [], isLoading } = useQuery({ 
    queryKey: ['risks', filters], 
    queryFn: () => api.getRisks(filters) 
  })
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets })
  const { data: securityControls = [] } = useQuery({ queryKey: ['security-controls'], queryFn: api.getSecurityControls })
  const { data: processes = [] } = useQuery({ queryKey: ['processes'], queryFn: api.getProcesses })
  const { data: annexControls = [] } = useQuery({ queryKey: ['annex-controls'], queryFn: api.getAnnexControls })

  const createMut = useMutation({
    mutationFn: api.createRisk,
    onSuccess: () => { qc.invalidateQueries(['risks']); setShowModal(false); toast('Risk registered', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateRisk(id, data),
    onSuccess: () => { qc.invalidateQueries(['risks']); setEditingId(null); toast('Risk updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })
   const bulkMut = useMutation({
    mutationFn: api.bulkCreateRisks,
    onSuccess: () => { qc.invalidateQueries(['risks']); toast('Risks imported successfully', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteRisk(id, reason),
    onSuccess: () => { qc.invalidateQueries(['risks']); toast('Risk moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const [sortConfig, setSortConfig] = useState({ key: 'custom_id', direction: 'asc' })
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const isContributor = user?.role === 'Contributor' || user?.role === 'User'
  const allowedRisks = isContributor ? risks.filter(r => { try { return JSON.parse(r.owner_ids || '[]').includes(user?.id) } catch { return false } }) : risks

  const sortedRisks = [...allowedRisks].sort((a, b) => {
    let aVal = a[sortConfig.key]
    let bVal = b[sortConfig.key]

    if (sortConfig.key === 'custom_id') {
      aVal = a.custom_id || `RSK-${String(a.id).padStart(4, '0')}`
      bVal = b.custom_id || `RSK-${String(b.id).padStart(4, '0')}`
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
    return 0
  })

  const exportToCSV = () => {
    const exportData = sortedRisks.map(r => {
      const asset = assets.find(a => a.id == r.asset_id)
      const displayId = r.custom_id || `RSK-${String(r.id).padStart(4, '0')}`
      return {
        'Risk ID': displayId,
        'Description': r.description,
        'Asset': asset?.asset_name || 'General',
        'Threat': r.threat,
        'Vulnerability': r.vulnerability,
        'Likelihood': r.likelihood,
        'Impact': r.impact,
        'Inherent Score': r.risk_score,
        'Residual Score': r.residual_risk.toFixed(1),
        'Treatment Strategy': r.treatment_type,
        'Action Plan': r.treatment_plan,
        'Owner': r.risk_owner,
        'Status': r.status,
        'Timeline': r.timeline,
        'Due Date': r.due_date || 'N/A',
        'Comments': r.comments,
        'Created Date': r.created_at,
        'Resolved Date': r.closed_at || 'N/A'
      }
    })
    
    const csv = Papa.unparse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `Risk_Register_${format(new Date(), 'yyyy-MM-dd')}.csv`)
    link.click()
  }

  const downloadTemplate = () => {
    const headers = [
      'Risk ID', 'Asset ID', 'Asset Name', 'Risk Description', 'Threat Type', 'Vulnerability',
      'Inherent Risk', 'Risk Level', 'Existing Controls', 'Additional Controls Required',
      'Annex A Mapping', 'Likelihood', 'Impact', 'Inherent Risk (LxI)', 'Control Strength',
      'Residual Risk (Value)', 'Risk Level (Residual)', 'Treatment Option', 'Residual Risk',
      'Owner', 'Timeline', 'Status', 'Comments'
    ]
    const example = [
      'RISK-001', 'IT-001', 'Microsoft 365', 'Credential theft via phishing', 'Human - Unintentional', 'Users click phishing links',
      '20', 'High', 'Awareness training, Email policy, MFA', 'Conditional Access, MDO anti-phishing',
      'A.5.17, A.6.3', '4', '5', '20', '5', '4', 'Low', 'Accept', 'Medium', 'Ashok', '30 Days', 'Planned', 'Initial assessment'
    ]
    const csv = Papa.unparse([headers, example])
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'risk_upload_template.csv'
    a.click()
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const mapped = results.data.map(row => {
          // Map Asset Name to ID if possible
          const foundAsset = assets.find(a => 
            a.asset_name?.toLowerCase() === row['Asset Name']?.toLowerCase() ||
            String(a.id) === String(row['Asset ID'])
          )
          
          return {
            description: row['Risk Description'] || row['description'] || 'Imported Risk',
            asset_id: foundAsset?.id || null,
            threat: row['Threat Type'] || row['threat'] || '',
            vulnerability: row['Vulnerability'] || row['vulnerability'] || '',
            likelihood: parseInt(row['Likelihood']) || 3,
            impact: parseInt(row['Impact']) || 3,
            treatment_type: ['Mitigate', 'Transfer', 'Accept', 'Avoid'].includes(row['Treatment Option']) ? row['Treatment Option'] : 'Mitigate',
            risk_owner: row['Owner'] || row['risk_owner'] || '',
            status: ['Open', 'In Progress', 'Mitigated', 'Accepted', 'Closed'].includes(row['Status']) ? row['Status'] : 'Open',
            timeline: row['Timeline'] || '',
            comments: row['Comments'] || '',
            // If they provided calculated scores, we can respect them or let backend re-calc
            risk_score: parseInt(row['Inherent Risk (LxI)']) || (parseInt(row['Likelihood']) * parseInt(row['Impact'])) || 0,
            residual_risk: parseFloat(row['Residual Risk (Value)']) || 0
          }
        })
        if (mapped.length > 0) bulkMut.mutate(mapped)
      }
    })
    e.target.value = '' // Reset input
  }

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Risk Register</h1>
          <p>Identification, Assessment (Controls Logic), and Treatment monitoring</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>
            <Download size={15} /> Template
          </button>
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <FileText size={15} /> Export Data
          </button>
          <div style={{ position: 'relative' }}>
            <button className="btn btn-secondary" onClick={() => document.getElementById('risk-csv').click()}>
              <Upload size={15} /> Bulk Upload
            </button>
            <input type="file" id="risk-csv" style={{ display: 'none' }} accept=".csv" onChange={handleFileUpload} />
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} /> Identify Risk
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{ 
        background: 'var(--bg-elevated)', borderRadius: 16, padding: '16px 24px', 
        border: '1px solid var(--border)', marginBottom: 24, display: 'flex', gap: 24, alignItems: 'end' 
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
            <Filter size={12} style={{ marginRight: 6 }} /> Filter by Status
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {STATUSES.map(s => {
              const active = filters.status.includes(s)
              return (
                <button 
                  key={s}
                  onClick={() => {
                    const next = active ? filters.status.filter(x => x !== s) : [...filters.status, s]
                    setFilters(f => ({ ...f, status: next }))
                  }}
                  style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: '0.8125rem', fontWeight: 600,
                    background: active ? 'var(--accent)' : 'var(--bg-base)',
                    color: active ? '#fff' : 'var(--text-secondary)',
                    border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
                    transition: 'all 0.2s', cursor: 'pointer'
                  }}
                >
                  {s}
                </button>
              )
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
              <Calendar size={12} style={{ marginRight: 6 }} /> From Date
            </label>
            <input 
              type="date" 
              className="form-input" 
              style={{ width: 160, padding: '6px 12px' }}
              value={filters.from}
              onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
              <Calendar size={12} style={{ marginRight: 6 }} /> To Date
            </label>
            <input 
              type="date" 
              className="form-input" 
              style={{ width: 160, padding: '6px 12px' }}
              value={filters.to}
              onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            />
          </div>
          <button 
            className="btn btn-ghost btn-sm" 
            style={{ marginBottom: 4 }}
            onClick={() => setFilters({ status: [], from: '', to: '' })}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('custom_id')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Risk ID {sortConfig.key === 'custom_id' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Description {sortConfig.key === 'description' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th>Asset</th>
              <th>Owner</th>
              <th onClick={() => handleSort('risk_score')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Inherent {sortConfig.key === 'risk_score' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th>Controls</th>
              <th onClick={() => handleSort('residual_risk')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Residual {sortConfig.key === 'residual_risk' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th onClick={() => handleSort('due_date')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Due Date {sortConfig.key === 'due_date' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Created {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th style={{ minWidth: 100 }}>Resolved</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedRisks.map(r => {
              const asset = assets.find(a => a.id == r.asset_id)
              return (
                <tr key={r.id}>
                  <td><span className="font-mono text-xs">{r.custom_id || `RSK-${String(r.id).padStart(4,'0')}`}</span></td>
                  <td><span style={{ fontWeight: 600 }}>{r.description}</span></td>
                  <td style={{ fontSize: '0.8125rem', maxWidth: 180 }}>
                    {(() => {
                      let aids = []
                      try {
                        aids = typeof r.asset_ids === 'string' ? JSON.parse(r.asset_ids) : (r.asset_ids || [])
                      } catch (e) { aids = r.asset_id ? [r.asset_id] : [] }
                      
                      if (aids.length === 0) return <span className="text-muted">—</span>
                      if (aids.length === 1) {
                        const a = assets.find(x => x.id == aids[0])
                        return a?.asset_name || 'General'
                      }
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          <span className="badge badge-neutral" style={{ fontSize: '0.7rem' }}>{aids.length} Assets</span>
                          <span className="truncate" style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block' }}>
                            {assets.filter(a => aids.includes(a.id)).map(a => a.asset_name).join(', ')}
                          </span>
                        </div>
                      )
                    })()}
                  </td>
                  <td style={{ fontSize: '0.8125rem' }}>{r.owner_name || '—'}</td>
                  <td><RiskScoreBadge score={r.risk_score} /></td>
                  <td><span className="badge badge-neutral">
                    {(() => {
                      let count = 0
                      try { count = JSON.parse(r.existing_control_ids || '[]').length } catch(e){}
                      return count
                    })()} active
                  </span></td>
                  <td><RiskScoreBadge score={r.residual_risk} /></td>
                  <td style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)' }}>{r.due_date ? format(new Date(r.due_date), 'dd MMM yyyy') : '—'}</td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.created_at ? format(new Date(r.created_at), 'dd MMM yyyy') : '—'}</td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.closed_at ? format(new Date(r.closed_at), 'dd MMM yyyy') : '—'}</td>
                  <td>
                    <span className={`badge ${
                      r.status === 'Open' ? 'badge-danger' : 
                      r.status === 'Accepted' || r.status === 'Mitigated' ? 'badge-success' : 
                      r.status === 'In Progress' ? 'badge-accent' :
                      'badge-neutral'
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" title="View History" onClick={() => setViewHistoryId(r.id)}><Eye size={14} /></button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(r.id)}><Edit size={14} /></button>
                      {user?.role !== 'Contributor' && user?.role !== 'User' && (
                        <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--danger)' }} onClick={() => setConfirmDelete(r)}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showModal && <RiskModal assets={assets} securityControls={securityControls} processes={processes} onClose={() => setShowModal(false)} onSave={d => createMut.mutate(d)} isSaving={createMut.isPending} user={user} />}
      {editingId && <RiskModal riskId={editingId} assets={assets} securityControls={securityControls} processes={processes} onClose={() => setEditingId(null)} onSave={d => updateMut.mutate({ id: editingId, data: d })} isSaving={updateMut.isPending} user={user} />}
      {viewHistoryId && <RiskLifecycleModal riskId={viewHistoryId} onClose={() => setViewHistoryId(null)} />}
      
      <ConfirmModal 
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={(reason) => deleteMut.mutate({ id: confirmDelete.id, reason })}
        title="Move to Bin?"
        message={`Delete risk "${confirmDelete?.description}"? It will be moved to the Recycle Bin and can be restored later.`}
        showReason={true}
      />
    </div>
  )
}
